import Image, { ImageLoaderProps } from "next/image";
import { MouseEventHandler, useEffect, useState } from "react";
import { PersistGate } from "redux-persist/integration/react";

import {
  DessertCategory,
  Formula,
  MainCategory,
  SidesCategory,
} from "../../../../api/types/restaurant.type";
import { roundPrice } from "../../../../api/utils/helpers/roundPrice";
import { translationsContent } from "../../../../localization/translations";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import {
  addNewFormulaToCart,
  removeFormulaFromCart,
  selectCart,
} from "../../../../redux/slices/cartSlice";
import { persistor } from "../../../../redux/store";
import { useLanguage } from "../../../../store/lang.context";
import { returnIndex } from "../../../../utils/returnIndex";
import { translate } from "../../../../utils/translate";
import CompositionModal from "../../../menupage/composition";
import Button from "../../../ui/button/SimpleButton";
import classes from "./ingredientFormulaCard.module.scss";

interface IngredientFormulaCardProps {
  id?: string;
  formula: Formula;
  categories: MainCategory[];
  sideCategories: SidesCategory[];
  isDessert?: boolean;
  desserts: DessertCategory[];
  hasDessert?: boolean;
  extraPrice?: number;
  outsideClick?: () => void;
  onImageClick?: () => void;
  onImageHover?: () => void;
  onCompositionToggle?: (toggle: boolean) => void;
  hasTitleVisible?: boolean;
  removeSlugs?: boolean;
  fullTitle?: boolean;
  justifySpaceBetween?: boolean;
  src?: string;
}

export const myLoader = ({ src, width, quality }: ImageLoaderProps): string => {
  return `${src}?w=${width}&q=${quality || 100}`;
};

const IngredientFormulaCard = ({
  id: textId,
  formula,
  categories,
  sideCategories,
  desserts,
  hasDessert = false,
  hasTitleVisible = true,
  extraPrice = 0,
  removeSlugs = false,
  fullTitle = false,
  justifySpaceBetween = false,
  outsideClick,
  onImageClick = () => {},
  onImageHover = () => {},
  onCompositionToggle = () => undefined,
  src,
}: IngredientFormulaCardProps) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [id, setId] = useState<number>(-1);

  const { language } = useLanguage();

  const dispatch = useAppDispatch();

  const cart = useAppSelector(selectCart);

  const tarte = formula.sides.find((side) =>
    side.title_fr.toLowerCase().includes("tarte salée")
  );

  const handleOpenModal = () => {
    dispatch(addNewFormulaToCart(formula));
    setId(cart.formulaItems.length);
    setModalVisible(true);
  };

  const handleCloseModal = (shouldRemoveFormulaFromCart: boolean) => {
    if (shouldRemoveFormulaFromCart)
      dispatch(removeFormulaFromCart({ formula: formula, id: id }));
    setModalVisible(false);
  };

  useEffect(() => {
    let timeoutId: NodeJS.Timeout = setTimeout(() => {}, 0);

    if (onImageHover && hovered) {
      timeoutId = setTimeout(() => {
        onImageHover();
      }, 500);
    } else {
      clearTimeout(timeoutId);
    }

    return () => clearTimeout(timeoutId);
  }, [hovered, onImageHover]);

  useEffect(() => {
    onCompositionToggle(modalVisible);
    return () => {
      onCompositionToggle(false);
    };
  }, [modalVisible]);

  const handleButtonClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();
    if (!outsideClick) handleOpenModal();
    else outsideClick();
  };

  return (
    <PersistGate persistor={persistor} loading={<>Loading...</>}>
      <div
        id={textId}
        className={`${classes.cardContainer} ${
          justifySpaceBetween ? classes.justifySpaceBetween : undefined
        }`}
        onClick={outsideClick ?? handleOpenModal}
      >
        <div className={classes.cardImageContainer}>
          <Image
            src={src ?? formula.image}
            height="120px"
            width="120px"
            alt={formula.title}
            loader={myLoader}
            onClick={(e) => {
              e.stopPropagation();
              onImageClick();
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
          />
        </div>

        <div className={classes.cardDescription}>
          {hasTitleVisible && (
            <h3 className={classes.cardTitle}>
              {formula[returnIndex(language)]
                ? fullTitle
                  ? formula[returnIndex(language)]
                  : formula[returnIndex(language)]
                      .split(" ")
                      .slice(0, 2)
                      .join(" ")
                : ""}
            </h3>
          )}
          {tarte && (
            <p className={`${classes.cardSide} ${classes.boldText}`}>
              {tarte[returnIndex(language)]}
            </p>
          )}
          {!removeSlugs && (
            <p className={classes.cardSlug}>
              {formula.num_of_base} Base
              <br />
              <span>
                {formula.num_of_ingredients}{" "}
                {categories[1] ? categories[1][returnIndex(language)] : ""}s
              </span>
              <br />
              {formula.num_of_sauce}{" "}
              {categories[2] ? categories[2][returnIndex(language)] : ""}
            </p>
          )}

          <div className={classes.cardSideContainer}>
            {formula.sides
              .filter(
                (side) => !side.title_fr.toLowerCase().includes("tarte salée")
              )
              .map((side, index) => {
                return (
                  <span key={index} className={classes.cardSide}>
                    + {side[returnIndex(language)]}
                  </span>
                );
              })}
          </div>
          <p className={classes.cardPrice}>
            {roundPrice(formula.price).toFixed(2)}€{" "}
            {extraPrice ? ` + ${extraPrice} €` : ""}
          </p>
        </div>
        {justifySpaceBetween ? (
          <div className={classes.cardButtonContainer}>
            <Button
              label={`${translate(translationsContent, "compose", language)}`}
              onClick={handleButtonClick}
            />
          </div>
        ) : (
          <Button
            label={`${translate(translationsContent, "compose", language)}`}
            onClick={handleButtonClick}
          />
        )}
      </div>

      {modalVisible && (
        <>
          <CompositionModal
            handleCloseModal={handleCloseModal}
            formula={formula}
            categories={categories}
            sides={formula.sides}
            sideCategories={sideCategories}
            id={id}
            hasDessert={hasDessert}
            desserts={desserts}
          />
        </>
      )}
    </PersistGate>
  );
};

export default IngredientFormulaCard;
